import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Container,
  Paper,
  Title,
  Table,
  Group,
  Select,
  NumberInput,
  TextInput,
  Text,
  Stack,
  ActionIcon,
  Divider,
  LoadingOverlay,
  Box,
  Badge,
  rem,
  Tabs,
  Loader,
  Modal,
  Tooltip,
  SegmentedControl,
  Menu,
  Checkbox,
} from "@mantine/core";
import { withDerived } from "../../utils/transactionMath";
import { translations } from "../../utils/translations";
import { MonthPickerInput } from "@mantine/dates";
import {
  IconReload,
  IconUser,
  IconChartBar,
  IconTable,
  IconCloudCheck,
  IconCloudUpload,
  IconSend,
  IconCloudDownload,
  IconSettings,
  IconColumns3,
} from "@tabler/icons-react";
import { transactionService } from "../../services/transactionService";
import useAuthStore from "../../store/useAuthStore";
import { notifications } from "@mantine/notifications";
import TransactionsDynamics from "./TransactionsDynamics";
import TransactionsInsights from "./TransactionsInsights";
import socketService from "../../services/socketService";

/* ─── colour tokens ─────────────────────────────────────────────────────── */
const C = {
  bg: "#f9fafb",
  surface: "#ffffff",
  border: "#e5e7eb",
  muted: "#6b7280",
  label: "#374151",
  heading: "#111827",
  indigo: "#665bd1",
  emerald: "#39876b",
  red: "#bd5555",
  amber: "#aa813e",
  theadBg: "#f7f7f9",
  theadText: "#6b7280",
  tfoot: "#f1effa",
};

const TH = {
  color: C.theadText,
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  padding: "10px 12px",
  borderBottom: `2px solid ${C.border}`,
  whiteSpace: "nowrap",
};

/* helper — build zero row for a date */
const emptyRow = (date) => ({
  date,
  profit: 0,
  keitaroProfit: 0,
  fluxarProfit: 0,
  spentIo1: 0,
  spentIo15: 0,
  anotherSpent: 0,
  spentIo1Pending: 0,
  spentIo15Pending: 0,
  comment: "",
});

/* ─── Column definitions — order fixed, visibility user-toggleable ────────
   `key` is stable (used for localStorage + visibility). `locked` columns
   cannot be hidden. Labels/tooltips resolve from translations at render. */
const COLUMN_DEFS = [
  { key: "date", labelKey: "date", align: "left", pl: 20, w: 90, locked: true },
  {
    key: "keitaro",
    labelKey: "keitaro",
    align: "right",
    w: 90,
    tooltipKey: "keitaro_tooltip",
  },
  { key: "fluxar", labelKey: "fluxar", align: "right", w: 90, tooltipKey: "fluxar_tooltip" },
  { key: "spentAll", labelKey: "spent_all", align: "right", w: 95 },
  {
    key: "netProfit",
    labelKey: "net_profit",
    align: "right",
    w: 95,
    tooltipKey: "net_profit_tooltip",
  },
  { key: "operatorShare", labelKey: "operator_share", align: "right", w: 110 },
  {
    key: "profit",
    labelKey: "profit",
    align: "right",
    w: 85,
    tooltipKey: "profit_tooltip",
  },
  { key: "roi", labelKey: "roi", align: "right", w: 100 },
  { key: "io", labelKey: "io", align: "right", w: 85 },
  { key: "ioDetailed", labelKey: "io_detailed", align: "right", w: 120 },
  { key: "otherSpent", labelKey: "other_spent", align: "right", w: 105 },
  {
    key: "pending",
    labelKey: "pending",
    align: "right",
    w: 110,
    tooltipKey: "pending_tooltip",
  },
  {
    key: "pendingNet",
    labelKey: "pending_net",
    align: "right",
    w: 120,
    tooltipKey: "pending_net_tooltip",
  },
  { key: "comment", labelKey: "comment", align: "left", pr: 20, w: 360, locked: true },
];

const COLUMN_STORAGE_KEY = "txVisibleCols";

/* build the default all-visible map */
const defaultColVisibility = () =>
  COLUMN_DEFS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {});

/* load persisted visibility, merged over defaults (so new columns appear) */
const loadColVisibility = () => {
  const base = defaultColVisibility();
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMN_STORAGE_KEY) || "{}");
    return { ...base, ...saved, date: true, comment: true };
  } catch {
    return base;
  }
};

/* ════════════════════════════════════════════════════════════════════════════ */
const Transactions = () => {
  const { user, isAdmin } = useAuthStore();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "uk");
  const t = translations[lang];

  const handleLangChange = (val) => {
    setLang(val);
    localStorage.setItem("lang", val);
  };

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedUserId, setSelectedUserId] = useState(
    user?.id ? String(user.id) : null,
  );
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState({}); // { date: fullRow }
  const [saveStatuses, setSaveStatuses] = useState({}); // { date: 'idle'|'dirty'|'saving'|'saved' }
  const [loading, setLoading] = useState(false);
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [monthlyShowcaseProfit, setMonthlyShowcaseProfit] = useState(0);
  const [tableWatchers, setTableWatchers] = useState([]); // emails watching this table

  const [fluxarGroups, setFluxarGroups] = useState([]);
  const [fluxarGroupsError, setFluxarGroupsError] = useState("");
  const [isSyncingFluxar, setIsSyncingFluxar] = useState(false);
  const [keitaroGroups, setKeitaroGroups] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [mappingModalOpened, setMappingModalOpened] = useState(false);
  const [kartaUsers, setKartaUsers] = useState([]);

  // Column visibility (persisted in localStorage)
  const [visibleCols, setVisibleCols] = useState(loadColVisibility);
  const toggleCol = (key) =>
    setVisibleCols((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  const visibleColumnDefs = COLUMN_DEFS.filter((c) => visibleCols[c.key]);

  // Timers map — not in state to avoid re-renders
  const timers = useRef({});

  const safeDate =
    selectedDate instanceof Date
      ? selectedDate
      : selectedDate
        ? new Date(selectedDate)
        : new Date();
  const month = safeDate.getMonth() + 1;
  const year = safeDate.getFullYear();

  /* ── Load users (admin only) ── */
  const loadUsers = useCallback(async () => {
    try {
      const res = await transactionService.getUsers();
      if (res.success) setUsers(res.users);
    } catch (e) {
      console.error(e);
    }
  }, []);

  /* ── Load transactions ── */
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const targetId = selectedUserId || user?.id;
      if (!targetId) {
        setRows({});
        return;
      }
      const res = await transactionService.getTransactions(
        month,
        year,
        targetId,
      );
      if (res.success) {
        // Index rows by date for O(1) lookup
        const map = {};
        res.transactions.forEach((t) => {
          map[t.date] = withDerived(t);
        });
        setRows(map);
        if (res.profitPercentage !== undefined)
          setCurrentPercentage(res.profitPercentage);
        if (res.showcaseProfit !== undefined)
          setMonthlyShowcaseProfit(res.showcaseProfit);
      }
    } catch (e) {
      console.error(e);
      notifications.show({
        title: t.error,
        message: t.error_load,
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, user?.id, month, year, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  /* ── WebSocket: join / leave transaction table room ── */
  useEffect(() => {
    const ownerId = selectedUserId || user?.id;
    if (!ownerId || !user?.email) return;

    socketService.joinTransactionTable(ownerId, user.email, user.id);

    const handleRowUpdate = ({ row, editorEmail }) => {
      // Skip if WE sent this update (avoid overwriting our own in-progress edits)
      if (editorEmail === user?.email) return;
      setRows((prev) => ({ ...prev, [row.date]: withDerived(row) }));
    };
    const handleWatchers = (list) => setTableWatchers(list);

    socketService.onTransactionRowUpdate(handleRowUpdate);
    socketService.onTransactionWatchers(handleWatchers);

    return () => {
      socketService.leaveTransactionTable(ownerId, user.email);
      socketService.offTransactionRowUpdate(handleRowUpdate);
      socketService.offTransactionWatchers(handleWatchers);
      setTableWatchers([]);
    };
  }, [selectedUserId, user?.id, user?.email]);

  /* ── Build all days of month ── */
  const daysInMonth = useMemo(() => {
    const daysCount = new Date(year, month, 0).getDate();
    return Array.from({ length: daysCount }, (_, i) => {
      const d = i + 1;
      const date = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      return rows[date] ?? emptyRow(date);
    });
  }, [rows, month, year]);

  /* ── Totals ── */
  const totals = useMemo(
    () =>
      daysInMonth.reduce(
        (acc, r) => {
          const profit = Number(r.profit) || 0;
          const keitaroProfit = Number(r.keitaroProfit) || 0;
          const fluxarProfit = Number(r.fluxarProfit) || 0;
          const spentIo1 = Number(r.spentIo1) || 0;
          const spentIo15 = Number(r.spentIo15) || 0;
          const anotherSpent = Number(r.anotherSpent) || 0;
          const spentIo1Pending = Number(r.spentIo1Pending) || 0;
          const spentIo15Pending = Number(r.spentIo15Pending) || 0;

          const totalPr = profit + keitaroProfit + fluxarProfit;
          const sp = spentIo1 + anotherSpent;
          const net = totalPr - sp;

          return {
            profit: acc.profit + profit,
            keitaroProfit: acc.keitaroProfit + keitaroProfit,
            fluxarProfit: acc.fluxarProfit + fluxarProfit,
            spentAll: acc.spentAll + sp,
            netProfit: acc.netProfit + net,
            spentIo1: acc.spentIo1 + spentIo1,
            spentIo15: acc.spentIo15 + spentIo15,
            anotherSpent: acc.anotherSpent + anotherSpent,
            spentIo1Pending: acc.spentIo1Pending + spentIo1Pending,
            spentIo15Pending: acc.spentIo15Pending + spentIo15Pending,
            pendingAll: acc.pendingAll + spentIo1Pending + spentIo15Pending,
          };
        },
        {
          profit: 0,
          keitaroProfit: 0,
          fluxarProfit: 0,
          spentAll: 0,
          netProfit: 0,
          spentIo1: 0,
          spentIo15: 0,
          anotherSpent: 0,
          spentIo1Pending: 0,
          spentIo15Pending: 0,
          pendingAll: 0,
        },
      ),
    [daysInMonth],
  );

  const totalROI =
    totals.spentAll > 0 ? (totals.netProfit / totals.spentAll) * 100 : 0;
  const userShare =
    (totals.netProfit * currentPercentage) / 100 - totals.spentIo15;
  const showcaseShare = (monthlyShowcaseProfit * currentPercentage) / 100;
  const userTotalWithShowcase = userShare + showcaseShare;

  /* ── Cell change — updates state immediately, queues debounced save ── */
  const handleCellChange = useCallback(
    (date, field, value) => {
      setRows((prev) => {
        const current = prev[date] ?? emptyRow(date);
        const updated = withDerived({ ...current, [field]: value });
        return { ...prev, [date]: updated };
      });
      setSaveStatuses((prev) => ({ ...prev, [date]: "dirty" }));

      // Reset + start debounce timer
      if (timers.current[date]) clearTimeout(timers.current[date]);
      timers.current[date] = setTimeout(() => {
        // Read from functional setter to always get latest state
        setRows((prev) => {
          const rowToSave = prev[date];
          if (!rowToSave) return prev;

          const targetId = selectedUserId || user?.id;
          if (!targetId) return prev;

          setSaveStatuses((s) => ({ ...s, [date]: "saving" }));

          transactionService
            .saveTransaction({ ...rowToSave, userId: targetId, month, year })
            .then(() => {
              setSaveStatuses((s) => ({ ...s, [date]: "saved" }));
              setTimeout(
                () => setSaveStatuses((s) => ({ ...s, [date]: "idle" })),
                2000,
              );
              // Broadcast to watchers
              socketService.emitTransactionRowUpdate(
                targetId,
                rowToSave,
                user?.email,
              );
            })
            .catch(() => {
              setSaveStatuses((s) => ({ ...s, [date]: "dirty" }));
              notifications.show({
                title: "Error",
                message: "Save failed",
                color: "red",
                autoClose: 3000,
              });
            });

          return prev; // no change to rows here
        });
      }, /* debounce */ 900);
    },
    [selectedUserId, user?.id, month, year, user?.email],
  );

  const handleSyncKeitaro = async () => {
    const targetId = selectedUserId || user?.id;
    if (!targetId) return;

    setIsSyncing(true);
    try {
      const res = await transactionService.syncKeitaro({
        userId: targetId,
        month: String(month),
        year: String(year),
      });
      if (res.success) {
        notifications.show({
          title: "Synced",
          message: `Data synced successfully`,
          color: "green",
          autoClose: 2000,
        });
        loadTransactions(); // Reload
      }
    } catch (e) {
      notifications.show({
        title: "Sync failed",
        message: e.response?.data?.error || "Error",
        color: "red",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncFluxar = async () => {
    const targetId = selectedUserId || user?.id;
    if (!targetId) return;
    setIsSyncingFluxar(true);
    try {
      const res = await transactionService.syncFluxar({ userId: targetId, month: String(month), year: String(year) });
      if (res.success) {
        notifications.show({ title: "Fluxar", message: t.fluxar_synced, color: "green" });
        loadTransactions();
      }
    } catch (error) {
      notifications.show({ title: "Fluxar", message: error.response?.data?.error || t.error, color: "red" });
    } finally { setIsSyncingFluxar(false); }
  };
  const loadFluxarGroups = useCallback(async () => {
    try {
      const res = await transactionService.getFluxarGroups();
      if (res.success) { setFluxarGroups(res.groups); setFluxarGroupsError(""); }
    } catch (error) { setFluxarGroupsError(error.response?.data?.error || t.error_load); }
  }, [t.error_load]);

  const loadKeitaroGroups = async () => {
    try {
      const res = await transactionService.getKeitaroGroups();
      if (res.success) setKeitaroGroups(res.groups);
    } catch (e) {}
  };

  const loadKartaUsers = async () => {
    try {
      const res = await transactionService.getKartaUsers();
      if (res.success) setKartaUsers(res.users);
    } catch (e) {}
  };

  useEffect(() => {
    if (isAdmin) {
      loadKeitaroGroups();
      loadFluxarGroups();
      loadKartaUsers();
    }
  }, [isAdmin, loadFluxarGroups]);

  /* ─── Shared table render ──────────────────────────────────────────── */
  const renderTable = (forAdmin) => (
    <Paper
      withBorder
      radius={5}
      style={{
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        boxShadow: "none",
      }}
    >
      <LoadingOverlay
        visible={loading}
        zIndex={10}
        overlayProps={{ blur: 2, color: "white", opacity: 0.7 }}
      />
      <Box className="transaction-ledger-scroll" role="region" aria-label="Transactions table" tabIndex={0}>
        <Table
          className="transaction-ledger"
          highlightOnHover
          verticalSpacing={0}
          horizontalSpacing={0}
          style={{
            // Keep every visible column's working width; the parent owns overflow.
            width: visibleColumnDefs.reduce((total, col) => total + col.w, 0),
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "auto",
          }}
        >
          {/* HEADER */}
          <Table.Thead style={{ backgroundColor: C.theadBg }}>
            <Table.Tr>
              {visibleColumnDefs.map((col) => {
                const label = t[col.labelKey];
                const tooltip = col.tooltipKey ? t[col.tooltipKey] : null;
                return (
                  <Table.Th
                    key={col.key}
                    style={{
                      ...TH,
                      textAlign: col.align,
                      paddingLeft: col.pl ?? 12,
                      paddingRight: col.pr ?? 16,
                      width: col.w,
                      minWidth: col.w,
                    }}
                  >
                    {tooltip ? (
                      <Tooltip label={tooltip} withArrow>
                        <Box style={{ cursor: "help" }}>{label}</Box>
                      </Tooltip>
                    ) : (
                      label
                    )}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>

          {/* BODY */}
          <Table.Tbody>
            {daysInMonth.map((row, idx) => {
              const targetId = selectedUserId || user?.id;
              const isReadOnly =
                !isAdmin && targetId && String(targetId) !== String(user?.id);
              const isDisabled = (isAdmin && !selectedUserId) || isReadOnly;

              return (
                <TableRow
                  key={row.date}
                  row={row}
                  saveStatus={saveStatuses[row.date] ?? "idle"}
                  onChange={(field, value) =>
                    handleCellChange(row.date, field, value)
                  }
                  disabled={isDisabled}
                  isEven={idx % 2 === 0}
                  currentPercentage={currentPercentage}
                  visibleCols={visibleCols}
                />
              );
            })}
          </Table.Tbody>

          {/* FOOTER */}
          <Table.Tfoot>
            <Table.Tr
              style={{
                backgroundColor: C.tfoot,
                borderTop: `2px solid ${C.indigo}33`,
              }}
            >
              {visibleColumnDefs.map((col) => {
                const money = (v, color) => (
                  <Table.Td
                    key={col.key}
                    style={{
                      textAlign: "right",
                      padding: "11px 16px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: color ?? C.heading,
                    }}
                  >
                    {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Table.Td>
                );

                switch (col.key) {
                  case "date":
                    return (
                      <Table.Td
                        key="date"
                        style={{
                          paddingLeft: 20,
                          paddingTop: 11,
                          paddingBottom: 11,
                          fontSize: 11,
                          fontWeight: 600,
                          color: C.indigo,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        {t.total}
                      </Table.Td>
                    );
                  case "keitaro":
                    return money(totals.keitaroProfit, C.indigo);
                  case "fluxar":
                    return money(totals.fluxarProfit, C.indigo);
                  case "spentAll":
                    return money(totals.spentAll);
                  case "netProfit":
                    return money(
                      totals.netProfit,
                      totals.netProfit >= 0 ? C.emerald : C.red,
                    );
                  case "operatorShare":
                    return (
                      <Table.Td
                        key="operatorShare"
                        style={{
                          textAlign: "right",
                          padding: "11px 16px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: userShare >= 0 ? C.emerald : C.red,
                          backgroundColor: `${C.indigo}08`,
                        }}
                      >
                        $
                        {userShare.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </Table.Td>
                    );
                  case "profit":
                    return money(totals.profit);
                  case "roi":
                    return (
                      <Table.Td
                        key="roi"
                        style={{ textAlign: "right", padding: "11px 16px" }}
                      >
                        <Badge
                          size="md"
                          variant="light"
                          color={totalROI >= 0 ? "green" : "red"}
                          radius="sm"
                          style={{ fontWeight: 800 }}
                        >
                          {totalROI.toFixed(1)}%
                        </Badge>
                      </Table.Td>
                    );
                  case "io":
                    return money(totals.spentIo1);
                  case "ioDetailed":
                    return money(totals.spentIo15);
                  case "otherSpent":
                    return money(totals.anotherSpent);
                  case "pending":
                    return (
                      <Table.Td
                        key="pending"
                        style={{
                          textAlign: "right",
                          padding: "11px 16px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.amber,
                          backgroundColor: `${C.amber}0d`,
                        }}
                      >
                        <Tooltip
                          withArrow
                          disabled={totals.pendingAll === 0}
                          label={`Spend: ${totals.spentIo1Pending.toLocaleString(
                            undefined,
                            { minimumFractionDigits: 2 },
                          )} · Fees: ${totals.spentIo15Pending.toLocaleString(
                            undefined,
                            { minimumFractionDigits: 2 },
                          )}`}
                        >
                          <span
                            style={{
                              cursor:
                                totals.pendingAll > 0 ? "help" : "default",
                            }}
                          >
                            {totals.pendingAll.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Tooltip>
                      </Table.Td>
                    );
                  case "pendingNet": {
                    // Same basis as NET(U): userShare minus pending impact.
                    const projTotal =
                      userShare -
                      (totals.spentIo1Pending * currentPercentage) / 100 -
                      totals.spentIo15Pending;
                    return money(
                      projTotal,
                      totals.pendingAll === 0
                        ? C.heading
                        : projTotal >= 0
                          ? C.emerald
                          : C.red,
                    );
                  }
                  case "comment":
                    return (
                      <Table.Td key="comment" style={{ paddingRight: 20 }} />
                    );
                  default:
                    return <Table.Td key={col.key} />;
                }
              })}
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Box>
    </Paper>
  );

  /* ─── Summary card ─────────────────────────────────────────────────── */
  const renderSummary = (showEditor) => (
    <Group justify="flex-end">
      <Paper
        withBorder
        p="xl"
        radius="xl"
        style={{
          minWidth: 400,
          backgroundColor: C.surface,
          border: `1px solid ${C.border}`,
          boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Stack gap="md">
          {showEditor ? (
            <Group grow align="flex-start" gap="md">
              <Box>
                <Text size="xs" fw={700} c="dimmed" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {t.operator_share_label}
                </Text>
                <NumberInput
                  mt={6}
                  size="md"
                  value={currentPercentage}
                  onChange={(v) => {
                    setCurrentPercentage(v || 0);
                    transactionService
                      .updateUserPercentage(selectedUserId, v || 0)
                      .then(() =>
                        notifications.show({
                          title: t.saved,
                          message: t.percentage_updated,
                          color: "green",
                          autoClose: 800,
                        }),
                      )
                      .catch(() =>
                        notifications.show({
                          title: "Error",
                          message: "Failed",
                          color: "red",
                        }),
                      );
                  }}
                  suffix="%"
                  variant="filled"
                  radius="md"
                  styles={{
                    input: {
                      fontWeight: 600,
                      color: C.indigo,
                      backgroundColor: `${C.indigo}0a`,
                      border: "none",
                    },
                  }}
                />
                <Text size="xs" color="dimmed" mt={4} fw={500}>{t.profit_split}</Text>
              </Box>

              <Box>
                <Text size="xs" fw={700} c="dimmed" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {t.showcase_profit}
                </Text>
                <NumberInput
                  mt={6}
                  min={0}
                  size="md"
                  disabled={
                    !isAdmin &&
                    selectedUserId &&
                    String(selectedUserId) !== String(user?.id)
                  }
                  value={monthlyShowcaseProfit}
                  onChange={(v) => {
                    const val = v || 0;
                    setMonthlyShowcaseProfit(val);
                    const targetId = selectedUserId || user?.id;
                    transactionService
                      .saveMonthlyStat({
                        month,
                        year,
                        showcase: val,
                        userId: targetId,
                      })
                      .then(() =>
                        notifications.show({
                          title: "Збережено",
                          message: t.showcase_updated,
                          color: "green",
                          autoClose: 800,
                        }),
                      )
                      .catch(() =>
                        notifications.show({
                          title: "Error",
                          message: "Failed",
                          color: "red",
                        }),
                      );
                  }}
                  prefix="$"
                  variant="filled"
                  radius="md"
                  styles={{
                    input: {
                      fontWeight: 600,
                      backgroundColor: C.bg,
                      border: "none",
                    },
                  }}
                />
              </Box>
            </Group>
          ) : (
            <Group justify="space-between" align="center">
              <Text size="xs" fw={700} c="dimmed" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {t.showcase_profit}
              </Text>
              <NumberInput
                min={0}
                size="sm"
                disabled={
                  !isAdmin &&
                  selectedUserId &&
                  String(selectedUserId) !== String(user?.id)
                }
                value={monthlyShowcaseProfit}
                onChange={(v) => {
                  const val = v || 0;
                  setMonthlyShowcaseProfit(val);
                  const targetId = selectedUserId || user?.id;
                  transactionService
                    .saveMonthlyStat({
                      month,
                      year,
                      showcase: val,
                      userId: targetId,
                    })
                    .then(() =>
                      notifications.show({
                        title: "Збережено",
                        message: t.showcase_updated,
                        color: "green",
                        autoClose: 800,
                      }),
                    )
                    .catch(() =>
                      notifications.show({
                        title: "Error",
                        message: "Failed",
                        color: "red",
                      }),
                    );
                }}
                prefix="$"
                style={{ width: 120 }}
                variant="filled"
                radius="md"
                styles={{
                  input: {
                    fontWeight: 600,
                    backgroundColor: C.bg,
                    border: "none",
                  },
                }}
              />
            </Group>
          )}

          <Paper
            p="lg"
            radius="lg"
            mt="xs"
            style={{
              backgroundColor: userTotalWithShowcase >= 0 ? `${C.emerald}08` : `${C.red}08`,
              border: `1px solid ${userTotalWithShowcase >= 0 ? `${C.emerald}22` : `${C.red}22`}`,
            }}
          >
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="sm" fw={800} style={{ color: userTotalWithShowcase >= 0 ? C.emerald : C.red }}>
                  {showEditor ? t.final_user_share : t.my_share}
                </Text>
                <Text size="xs" color="dimmed" mt={4} fw={600}>
                  ({t.calculation_formula.replace("{val}", currentPercentage)}) + Showcase
                </Text>
              </Box>
              <Text
                fw={900}
                style={{
                  fontSize: rem(34),
                  color: userTotalWithShowcase >= 0 ? C.emerald : C.red,
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                }}
              >
                ${userTotalWithShowcase.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </Group>
            
            <Divider my="md" style={{ borderColor: userTotalWithShowcase >= 0 ? `${C.emerald}1a` : `${C.red}1a` }} />
            
            <Group justify="space-between" align="center">
              <Text size="sm" fw={700} style={{ color: C.heading }}>
                {lang === "uk" ? "Без вітрини (Showcase)" : "Without Showcase"}
              </Text>
              <Text fw={800} size="lg" style={{ color: C.heading, letterSpacing: "-0.02em" }}>
                ${userShare.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </Group>
          </Paper>
        </Stack>
      </Paper>
    </Group>
  );

  /* ─── Toolbar ──────────────────────────────────────────────────────── */
  const renderToolbar = (forAdmin) => (
    <Group justify="flex-end">
      <Group gap="sm" style={{ flex: 1, flexWrap: "wrap" }}>
        {/* Watchers strip */}
        {tableWatchers.length > 0 && (
          <Group gap={6}>
            {tableWatchers.map((email) => (
              <Badge
                key={email}
                size="sm"
                variant="light"
                color="violet"
                leftSection={<span style={{ fontSize: 10 }}>👁</span>}
                styles={{ root: { textTransform: "none" } }}
              >
                {email}
              </Badge>
            ))}
          </Group>
        )}
      </Group>
      <Group gap="sm">
        <SegmentedControl
          size="xs"
          radius="md"
          value={lang}
          onChange={handleLangChange}
          data={[
            { label: "🇺🇦", value: "uk" },
            { label: "🇺🇸", value: "en" },
          ]}
        />
        {users.length > 1 && (
          <Select
            placeholder={t.select_operator}
            data={users.map((u) => ({ value: String(u.id), label: u.email }))}
            value={selectedUserId ? String(selectedUserId) : null}
            onChange={setSelectedUserId}
            searchable
            leftSection={<IconUser size={15} color={C.muted} />}
            variant="filled"
            radius="md"
            size="sm"
            style={{ width: 230 }}
            styles={{ input: { fontWeight: 600, backgroundColor: C.bg } }}
          />
        )}
        <MonthPickerInput
          placeholder={t.month}
          value={selectedDate}
          onChange={setSelectedDate}
          variant="filled"
          radius="md"
          size="sm"
          style={{ width: 140 }}
          styles={{ input: { fontWeight: 600, backgroundColor: C.bg } }}
        />
        <Menu shadow="md" width={220} closeOnItemClick={false} position="bottom-end">
          <Menu.Target>
            <ActionIcon
              size="md"
              variant="light"
              color="gray"
              radius="md"
              title={t.columns_settings}
            >
              <IconColumns3 size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t.columns_settings}</Menu.Label>
            {COLUMN_DEFS.map((col) => (
              <Menu.Item key={col.key} disabled={col.locked}>
                <Checkbox
                  size="xs"
                  checked={visibleCols[col.key] !== false}
                  disabled={col.locked}
                  onChange={() => toggleCol(col.key)}
                  label={t[col.labelKey]}
                  styles={{ label: { fontSize: 13, fontWeight: 500 } }}
                />
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>

        <ActionIcon
          size="md"
          variant="light"
          color="indigo"
          onClick={loadTransactions}
          loading={loading}
          radius="md"
          title={t.reload}
        >
          <IconReload size={16} />
        </ActionIcon>

        <ActionIcon
          size="md"
          variant="light"
          color="blue"
          onClick={handleSyncKeitaro}
          loading={isSyncing}
          radius="md"
          title={t.sync_keitaro}
        >
          <IconCloudDownload size={16} />
        </ActionIcon>
        <ActionIcon size="md" variant="light" color="teal" onClick={handleSyncFluxar}
          loading={isSyncingFluxar} radius="md" title={t.sync_fluxar} aria-label={t.sync_fluxar}>
          <IconCloudDownload size={16} />
        </ActionIcon>

        {isAdmin && (
          <ActionIcon
            size="md"
            variant="light"
            color="orange"
            radius="md"
            title={t.mapping}
            onClick={() => setMappingModalOpened(true)}
          >
            <IconSettings size={16} />
          </ActionIcon>
        )}
        {isAdmin && (
          <ActionIcon
            size="md"
            variant="light"
            color="teal"
            radius="md"
            title={t.backup}
            onClick={() => {
              notifications.show({
                title: t.sending,
                message: t.preparing_backup,
                color: "blue",
                autoClose: 2000,
              });
              transactionService
                .sendBackup({ month, year })
                .then(() =>
                  notifications.show({
                    title: t.sent_success,
                    message: t.sent_msg,
                    color: "green",
                  }),
                )
                .catch(() =>
                  notifications.show({
                    title: t.error,
                    message: t.error_backup,
                    color: "red",
                  }),
                );
            }}
          >
            <IconSend size={16} />
          </ActionIcon>
        )}
      </Group>
    </Group>
  );

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <Container size="100%" p={0}>
      <Stack gap="xl">
        <Box>
          <Title
            order={2}
            style={{
              color: C.heading,
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            {t.transactions}
          </Title>
          <Text size="sm" mt={4} style={{ color: C.muted }}>
            {t.dashboard_title}
          </Text>
        </Box>

        {isAdmin ? (
          <Tabs
            defaultValue="table"
            variant="pills"
            radius="md"
            styles={{
              tab: {
                fontWeight: 600,
                fontSize: 13,
                paddingTop: 7,
                paddingBottom: 7,
              },
              list: {
                backgroundColor: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 7,
                padding: 4,
                gap: 4,
                width: "fit-content",
                marginBottom: 24,
              },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="table" leftSection={<IconTable size={15} />}>
                {t.data_table}
              </Tabs.Tab>
              <Tabs.Tab
                value="dynamics"
                leftSection={<IconChartBar size={15} />}
              >
                {t.analytics}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="table">
              <Stack gap="lg">
                {renderToolbar(true)}
                <TransactionsInsights daysInMonth={daysInMonth} t={t} />
                {renderTable(true)}
                {renderSummary(true)}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="dynamics">
              <TransactionsDynamics users={users} t={t} />
            </Tabs.Panel>
          </Tabs>
        ) : (
          <Stack gap="lg">
            {renderToolbar(false)}
            <TransactionsInsights daysInMonth={daysInMonth} t={t} />
            {renderTable(false)}
            {renderSummary(false)}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={mappingModalOpened}
        onClose={() => setMappingModalOpened(false)}
        title={t.mapping_title}
        size="xl"
      >
        <Stack>
          <Text size="sm" color="dimmed">
            {t.mapping_desc}
          </Text>
          {fluxarGroupsError && <Text size="sm" c="red">Fluxar: {fluxarGroupsError}</Text>}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t.user}</Table.Th>
                <Table.Th>{t.keitaro_group}</Table.Th>
                <Table.Th>{t.fluxar_group}</Table.Th>
                <Table.Th>{t.karta_id}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>{u.email}</Table.Td>
                  <Table.Td>
                    <Select
                      data={[
                        { value: "0", label: t.none },
                        ...keitaroGroups.map((g) => ({
                          value: String(g.id),
                          label: g.name,
                        })),
                      ]}
                      value={String(u.keitaroGroupId || 0)}
                      onChange={(val) => {
                        transactionService
                          .updateUserKeitaroGroup(u.id, parseInt(val))
                          .then(() => {
                            loadUsers();
                            notifications.show({
                              title: t.saved,
                              message: t.mapping_saved.replace(
                                "{val}",
                                u.email,
                              ),
                              color: "green",
                              autoClose: 500,
                            });
                          });
                      }}
                      searchable
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select aria-label={`${t.fluxar_group}: ${u.email}`} data={[{ value: "", label: t.none },
                      ...fluxarGroups.map(g => ({ value: String(g.id), label: g.name }))]}
                      value={u.fluxarGroupId || ""} searchable size="xs"
                      onDropdownOpen={loadFluxarGroups}
                      onChange={value => {
                        transactionService.updateUserFluxarGroup(u.id, value || null).then(() => {
                          loadUsers();
                          notifications.show({ title: t.saved, message: t.mapping_saved.replace("{val}", u.email), color: "green", autoClose: 500 });
                        }).catch(error => notifications.show({ title: t.error, message: error.response?.data?.error || t.error, color: "red" }));
                      }} />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={[
                        { value: "0", label: t.none },
                        ...kartaUsers.map((ku) => ({
                          value: String(ku.id),
                          label: ku.name,
                        })),
                      ]}
                      value={u.kartaId ? String(u.kartaId) : "0"}
                      onChange={(val) => {
                        transactionService
                          .updateUserKartaId(u.id, val === "0" ? null : val)
                          .then(() => {
                            loadUsers();
                            notifications.show({
                              title: t.saved,
                              message: t.karta_saved.replace("{val}", u.email),
                              color: "green",
                              autoClose: 500,
                            });
                          });
                      }}
                      searchable
                      size="xs"
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Modal>
    </Container>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   PURELY PRESENTATIONAL — no local state, no timers, no save logic
   ═══════════════════════════════════════════════════════════════════════════ */
const TableRow = ({
  row,
  saveStatus,
  onChange,
  disabled,
  isEven,
  currentPercentage,
  visibleCols = {},
}) => {
  const [focusedField, setFocusedField] = useState(null);

  const show = (key) => visibleCols[key] !== false;

  const totalProfit = (row.profit || 0) + (row.keitaroProfit || 0) + (row.fluxarProfit || 0);
  const spentAll = (row.spentIo1 || 0) + (row.anotherSpent || 0);
  const netProfit = totalProfit - spentAll;
  const roi = spentAll > 0 ? (netProfit / spentAll) * 100 : 0;
  const netProfitUser =
    (netProfit * currentPercentage) / 100 - (row.spentIo15 || 0);
  const pendingSpentAll =
    (row.spentIo1Pending || 0) + (row.spentIo15Pending || 0);
  // Projected USER share (NET U) if the pending Karta spend/fees settle.
  // Pending spend reduces netProfit → only ×% reaches the user; pending fees
  // hit the user in full (same as spentIo15). Always ≤ NET(U) — pending is a cost.
  const projectedNet =
    netProfitUser -
    ((row.spentIo1Pending || 0) * currentPercentage) / 100 -
    (row.spentIo15Pending || 0);

  const dateObj = new Date(row.date);
  const formattedDate = isNaN(dateObj.getTime())
    ? row.date
    : dateObj.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" });

  const statusIcon =
    saveStatus === "dirty" ? (
      <IconCloudUpload size={13} color={C.amber} style={{ opacity: 0.8 }} />
    ) : saveStatus === "saving" ? (
      <Loader size={11} color="blue" />
    ) : saveStatus === "saved" ? (
      <IconCloudCheck size={14} color={C.emerald} />
    ) : null;

  const rowBg = focusedField ? `${C.indigo}07` : isEven ? "#fafafa" : "#ffffff";

  const inp = (positive, fieldName) => ({
    input: {
      textAlign: "right",
      fontWeight: 600,
      fontSize: 13,
      height: 32,
      minHeight: 32,
      padding: "0 8px",
      border:
        focusedField === fieldName
          ? `1.5px solid ${C.indigo}`
          : `1px solid ${C.border}`,
      borderRadius: 6,
      backgroundColor:
        focusedField === fieldName
          ? `${C.indigo}0d`
          : positive
            ? "rgba(5,150,105,0.05)"
            : C.surface,
      color: positive ? C.emerald : C.heading,
      boxShadow:
        focusedField === fieldName ? `0 0 0 3px ${C.indigo}1a` : "none",
      transition:
        "border-color 0.12s, box-shadow 0.12s, background-color 0.12s",
    },
  });

  return (
    <Table.Tr
      style={{
        backgroundColor: rowBg,
        borderBottom: `1px solid ${C.border}`,
        transition: "background-color 0.15s",
        outline: focusedField ? `2px solid ${C.indigo}55` : "none",
      }}
    >
      {/* DATE — always visible */}
      <Table.Td style={{ paddingLeft: 20, paddingTop: 5, paddingBottom: 5 }}>
        <Group gap={6} wrap="nowrap">
          <Text
            size="sm"
            fw={600}
            style={{ color: C.label, fontVariantNumeric: "tabular-nums" }}
          >
            {formattedDate}
          </Text>
          <Box
            className="ledger-value"
            style={{
              width: 14,
              height: 14,
              display: "flex",
              alignItems: "center",
            }}
          >
            {statusIcon}
          </Box>
        </Group>
      </Table.Td>
      {/* KEITARO PROFIT — read-only */}
      {show("keitaro") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: (row.keitaroProfit || 0) > 0 ? C.emerald : C.heading,
              backgroundColor: C.surface,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              opacity: 0.8,
            }}
          >
            {(row.keitaroProfit || 0).toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* FLUXAR PROFIT — read-only */}
      {show("fluxar") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: (row.fluxarProfit || 0) > 0 ? C.emerald : C.heading,
              backgroundColor: C.surface,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              opacity: 0.8,
            }}
          >
            {(row.fluxarProfit || 0).toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* SPENT ALL — computed, read-only */}
      {show("spentAll") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: C.muted,
              backgroundColor: C.bg,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
            }}
          >
            {spentAll.toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* NET PROFIT — computed, read-only */}
      {show("netProfit") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 700,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: netProfit >= 0 ? C.emerald : C.red,
              backgroundColor:
                netProfit >= 0
                  ? "rgba(5,150,105,0.05)"
                  : "rgba(220,38,38,0.05)",
              borderRadius: 6,
              border: `1px solid ${netProfit >= 0 ? "rgba(5,150,105,0.2)" : "rgba(220,38,38,0.2)"}`,
            }}
          >
            {netProfit.toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* NET PROFIT (USER) */}
      {show("operatorShare") && (
        <Table.Td style={{ padding: "3px 8px", textAlign: "right" }}>
          <Box style={{ textAlign: "right" }}>
            <Text
              fw={700}
              size="sm"
              style={{
                color: netProfitUser >= 0 ? C.emerald : C.red,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {netProfitUser.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          </Box>
        </Table.Td>
      )}

      {/* PROFIT */}
      {show("profit") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <NumberInput
            size="xs"
            variant="unstyled"
            value={
              row.profit === 0
                ? focusedField === "profit"
                  ? ""
                  : 0
                : row.profit || 0
            }
            onChange={(v) => onChange("profit", v || 0)}
            onFocus={() => setFocusedField("profit")}
            onBlur={() => setFocusedField(null)}
            min={0}
            allowNegative={false}
            hideControls
            disabled={disabled}
            styles={inp((row.profit || 0) > 0, "profit")}
          />
        </Table.Td>
      )}

      {/* ROI */}
      {show("roi") && (
        <Table.Td style={{ padding: "3px 16px 3px 8px", textAlign: "right" }}>
          <Badge
            size="sm"
            variant="light"
            color={roi >= 0 ? "green" : "red"}
            radius="sm"
            style={{ fontWeight: 700, minWidth: rem(58) }}
          >
            {roi.toFixed(1)}%
          </Badge>
        </Table.Td>
      )}

      {/* IO 1% (Spent IO) — read-only */}
      {show("io") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: C.heading,
              backgroundColor: C.surface,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              opacity: 0.8,
            }}
          >
            {(row.spentIo1 || 0).toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* IO 1.5% (Fees) — read-only */}
      {show("ioDetailed") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 600,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color: C.heading,
              backgroundColor: C.surface,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              opacity: 0.8,
            }}
          >
            {(row.spentIo15 || 0).toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* OTHER */}
      {show("otherSpent") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <NumberInput
            size="xs"
            variant="unstyled"
            value={
              row.anotherSpent === 0
                ? focusedField === "anotherSpent"
                  ? ""
                  : 0
                : row.anotherSpent || 0
            }
            onChange={(v) => onChange("anotherSpent", v || 0)}
            onFocus={() => setFocusedField("anotherSpent")}
            onBlur={() => setFocusedField(null)}
            min={0}
            allowNegative={false}
            hideControls
            disabled={disabled}
            styles={inp(false, "anotherSpent")}
          />
        </Table.Td>
      )}

      {/* PENDING (Karta) — read-only, amber = still hanging */}
      {show("pending") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Tooltip
            label={`Spend: ${(row.spentIo1Pending || 0).toFixed(2)} · Fees: ${(row.spentIo15Pending || 0).toFixed(2)}`}
            withArrow
            disabled={pendingSpentAll === 0}
          >
            <Box
            className="ledger-value"
              style={{
                textAlign: "right",
                fontWeight: 700,
                fontSize: 13,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 8,
                color: pendingSpentAll > 0 ? C.amber : C.muted,
                backgroundColor:
                  pendingSpentAll > 0 ? `${C.amber}0d` : C.bg,
                borderRadius: 6,
                border: `1px solid ${pendingSpentAll > 0 ? `${C.amber}55` : C.border}`,
                cursor: pendingSpentAll > 0 ? "help" : "default",
              }}
            >
              {pendingSpentAll.toFixed(2)}
            </Box>
          </Tooltip>
        </Table.Td>
      )}

      {/* NET IF PENDING SETTLES — projected, read-only */}
      {show("pendingNet") && (
        <Table.Td style={{ padding: "3px 8px" }}>
          <Box
            className="ledger-value"
            style={{
              textAlign: "right",
              fontWeight: 700,
              fontSize: 13,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              color:
                pendingSpentAll === 0
                  ? C.muted
                  : projectedNet >= 0
                    ? C.emerald
                    : C.red,
              backgroundColor:
                pendingSpentAll === 0
                  ? C.bg
                  : projectedNet >= 0
                    ? "rgba(5,150,105,0.05)"
                    : "rgba(220,38,38,0.05)",
              borderRadius: 6,
              border: `1px solid ${pendingSpentAll === 0 ? C.border : projectedNet >= 0 ? "rgba(5,150,105,0.2)" : "rgba(220,38,38,0.2)"}`,
            }}
          >
            {projectedNet.toFixed(2)}
          </Box>
        </Table.Td>
      )}

      {/* COMMENT — always visible */}
      <Table.Td style={{ padding: "3px 8px", paddingRight: 20, minWidth: 360 }}>
        <TextInput
          size="xs"
          aria-label={`Comment ${formattedDate}`}
          variant="unstyled"
          value={row.comment || ""}
          onChange={(e) => onChange("comment", e.currentTarget.value)}
          onFocus={() => setFocusedField("comment")}
          onBlur={() => setFocusedField(null)}
          placeholder="—"
          disabled={disabled}
          styles={{
            input: {
              fontSize: 13,
              fontWeight: 500,
              height: 32,
              minHeight: 32,
              padding: "0 8px",
              border:
                focusedField === "comment"
                  ? `1.5px solid ${C.indigo}`
                  : `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.label,
              backgroundColor:
                focusedField === "comment" ? `${C.indigo}0d` : C.surface,
              boxShadow:
                focusedField === "comment" ? `0 0 0 3px ${C.indigo}1a` : "none",
              transition:
                "border-color 0.12s, box-shadow 0.12s, background-color 0.12s",
            },
          }}
        />
      </Table.Td>
    </Table.Tr>
  );
};

export default Transactions;
